import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Symbol } from './Symbol'
import { type SlotSymbol, SYMBOLS } from '../utils/slotsLogic'

interface ReelProps {
  symbols: SlotSymbol[]
  isSpinning: boolean
  reelIndex: number
  winningRows: number[]
  spinDuration?: number
}

const SPIN_SYMBOLS: SlotSymbol[] = ['gun', 'dice', 'cards', 'diamond', 'whiskey', 'cigar', 'wild', 'scatter']

export function Reel({ symbols, isSpinning, reelIndex, winningRows, spinDuration = 2 }: ReelProps) {
  const delay = reelIndex * 0.2

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        'w-[60px] md:w-[72px]',
        'h-[180px] md:h-[204px]', // Fixed height for 3 symbols
        'rounded-xl',
        'bg-gradient-to-b from-zinc-950 via-black to-zinc-950',
        'border-2 border-zinc-700',
        'shadow-[inset_0_0_20px_rgba(0,0,0,0.8)]'
      )}
    >
      {/* Top Shadow Overlay */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black via-black/80 to-transparent z-20 pointer-events-none" />

      {/* Bottom Shadow Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black via-black/80 to-transparent z-20 pointer-events-none" />

      {/* Center Line Highlight */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[60px] md:h-[68px] border-y border-casino-gold/20 bg-casino-gold/5 z-10 pointer-events-none" />

      {/* Reel Content */}
      <div className="relative h-full">
        <AnimatePresence mode="wait">
          {isSpinning ? (
            <motion.div
              key="spinning"
              className="absolute inset-0 flex flex-col"
              initial={{ y: 0 }}
              animate={{ y: '-50%' }}
              transition={{
                duration: 0.15,
                repeat: Infinity,
                ease: 'linear',
                delay,
              }}
            >
              {/* Spinning symbols - duplicated for seamless loop */}
              {[...Array(2)].map((_, setIndex) => (
                <div key={setIndex} className="flex flex-col">
                  {SPIN_SYMBOLS.map((symbolKey, i) => (
                    <div
                      key={`${setIndex}-${i}`}
                      className={cn(
                        'flex items-center justify-center',
                        'w-[60px] h-[60px] md:w-[72px] md:h-[68px]',
                        'text-3xl md:text-4xl'
                      )}
                    >
                      <span className="blur-[1px] opacity-70">
                        {SYMBOLS[symbolKey].emoji}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="stopped"
              className="flex flex-col h-full justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25,
                delay: delay + spinDuration * 0.8,
              }}
            >
              {symbols.map((symbol, rowIndex) => (
                <Symbol
                  key={`${reelIndex}-${rowIndex}`}
                  symbol={symbol}
                  isWinning={winningRows.includes(rowIndex)}
                  size="md"
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Glass Reflection Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none rounded-xl z-30" />

      {/* Side Shine */}
      <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-r from-white/10 to-transparent pointer-events-none z-30" />
    </div>
  )
}
